import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

/**
 * Fetch protocol with splits and exercises from Firestore (mirror of mobile CoachFirebase.getExpandedProtocol).
 * Paths: organizations/{organizationId}/protocols/{protocolId},
 *        organizations/{organizationId}/protocolSplits (where protocolId),
 *        organizations/{organizationId}/protocolExercises (where splitId) per split.
 *
 * @param {string} organizationId
 * @param {string} protocolId
 * @returns {Promise<{ protocolData?: object, protocolSplits?: array, splitsExercises?: array }>}
 */
export async function getExpandedProtocol({ organizationId, protocolId }) {
  if (!organizationId || !protocolId) {
    throw new Error("getExpandedProtocol requires organizationId and protocolId");
  }

  const db = firebase.firestore();

  const protocolRef = db
    .collection("organizations")
    .doc(organizationId)
    .collection("protocols")
    .doc(protocolId);

  const splitsRef = db
    .collection("organizations")
    .doc(organizationId)
    .collection("protocolSplits")
    .where("protocolId", "==", protocolId);

  const [protocolSnap, splitsSnap] = await Promise.all([
    protocolRef.get(),
    splitsRef.get(),
  ]);

  const splitDataValues = splitsSnap.docs.map((d) => {
    const data = d.data();
    return { ...data, id: d.id };
  });
  const sortedSplits = [...splitDataValues].sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0)
  );

  const splitsExercisesPromises = sortedSplits.map(async (split) => {
    const exercisesRef = db
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .where("splitId", "==", split.id)
      .orderBy("index");

    const exercisesSnap = await exercisesRef.get();
    const exercises = exercisesSnap.docs.map((d) => {
      const data = d.data();
      return { ...data, id: d.id };
    });
    return { ...split, exercises };
  });

  const splitsExercises = await Promise.all(splitsExercisesPromises);

  const result = {};
  if (protocolSnap.exists) {
    const protocolData = protocolSnap.data();
    result.protocolData = normalizeTimestamps(protocolData);
    result.protocolSplits = sortedSplits.map(normalizeTimestamps);
    result.splitsExercises = splitsExercises.map((s) => ({
      ...normalizeTimestamps(s),
      exercises: (s.exercises || []).map(normalizeTimestamps),
    }));
  }

  return result;
}

function normalizeTimestamps(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  ["createdOn", "updatedOn", "completedOn"].forEach((key) => {
    if (out[key] && typeof out[key].toDate === "function") {
      out[key] = out[key].toDate();
    }
  });
  return out;
}
